import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { NpcId, ProductId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { Reward } from '@/core/content/schemas/progression';
import type { MailboxCycle } from '@/core/content/schemas/progressionDefs';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import type { HandlerEnv } from './placement/handlers';
import { grantRewards } from './rewards';
import { dayKeyOf } from './time';

/**
 * Hòm thư (content-schemas §14.5): one gift per calendar day, a deterministic 7-day cycle that advances per
 * grant. Safe against clock rollback: a day that is not after the last granted day grants nothing.
 */
type MailItem = GameSave['mailbox']['pending'][number];

const DAY_MS = 86_400_000;
const dayIndex = (key: string): number => {
  const [y, m, d] = key.split('-').map(Number);
  return Math.round(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / DAY_MS);
};
const fromIndex = (i: number): string => new Date(i * DAY_MS).toISOString().slice(0, 10);

function pendingRewards(s: GameSave): Reward[] {
  return [...s.mailbox.pending, ...s.mailbox.special].flatMap((m) => m.rewards);
}

function materialize(
  draft: GameSave,
  content: ContentRegistry,
  cycle: MailboxCycle,
  cycleDay: number,
): { rewards: Reward[]; kindForUi: MailItem['kindForUi'] } {
  const M = draft.mailbox;
  const day = cycle.days.find((d) => d.day === cycleDay) ?? cycle.days[0];
  const rewards: Reward[] = [];
  const kinds = new Set<string>();
  for (const e of day?.entries ?? []) {
    kinds.add(e.kind);
    switch (e.kind) {
      case 'postcard': {
        const idx = M.postcardCursor;
        const id = cycle.postcardOrder[idx % cycle.postcardOrder.length] ?? 'pc_1';
        rewards.push({ type: 'postcard', postcardId: id, isRepeat: idx >= cycle.postcardOrder.length });
        M.postcardCursor += 1;
        break;
      }
      case 'coins':
        rewards.push({ type: 'coins', amount: e.amount });
        break;
      case 'miniBox': {
        const unlocked = [...content.items.values()]
          // Homemade dishes come from Bếp của bà only, never in a mail box.
          .filter((d) => d.kind === 'product' && (!d.unlockId || draft.unlockedIds.includes(d.unlockId)))
          .filter((d) => !d.tags.includes('homemade'))
          .map((d) => d.id as ProductId)
          .sort();
        const pool: readonly ProductId[] = unlocked.length > 0 ? unlocked : cycle.fallbackProducts;
        const k = Math.min(e.productKinds, pool.length);
        const start = (M.miniBoxCursor * e.productKinds) % pool.length;
        for (let j = 0; j < k; j++) {
          const itemId = pool[(start + j) % pool.length];
          if (itemId) rewards.push({ type: 'item', itemId, qty: e.unitsEach });
        }
        M.miniBoxCursor += 1;
        break;
      }
      case 'npcLetter': {
        const pre = cycle.preChapter2Letter;
        const have = (id: `ltr_${string}`) =>
          draft.story.letters.includes(id) ||
          pendingRewards(draft).some((r) => r.type === 'letter' && r.letterId === id);
        if (!draft.flags[pre.requiresFlag]) {
          rewards.push(
            have(pre.letterId)
              ? { type: 'coins', amount: pre.fallbackCoins }
              : { type: 'letter', letterId: pre.letterId },
          );
          break;
        }
        let found = false;
        for (let tries = 0; tries < e.rotation.length; tries++) {
          const npc: NpcId | undefined = e.rotation[(M.letterCursor + tries) % e.rotation.length];
          const next = [...content.letters.values()]
            .filter(
              (l) =>
                l.from === npc &&
                !have(l.id) &&
                !content.neighborhoodStories.some((s) => s.inviteLetterId === l.id),
            )
            .sort((a, b) => a.order - b.order)[0];
          if (next) {
            rewards.push({ type: 'letter', letterId: next.id });
            M.letterCursor += tries + 1;
            found = true;
            break;
          }
        }
        if (!found) {
          rewards.push({ type: 'coins', amount: pre.fallbackCoins });
          M.letterCursor += 1;
        }
        break;
      }
      case 'sticker': {
        const pending = pendingRewards(draft);
        const s = cycle.stickerPool.find(
          (id) =>
            !draft.cosmetics.stickers.includes(id) &&
            !pending.some((r) => r.type === 'sticker' && r.stickerId === id),
        );
        rewards.push(
          s ? { type: 'sticker', stickerId: s } : { type: 'coins', amount: cycle.stickerExhaustedCoins },
        );
        break;
      }
    }
  }
  const kindForUi: MailItem['kindForUi'] =
    kinds.has('postcard') && kinds.has('miniBox')
      ? 'postcardAndBox'
      : kinds.has('postcard')
        ? 'postcard'
        : kinds.has('miniBox')
          ? 'miniBox'
          : kinds.has('npcLetter')
            ? 'npcLetter'
            : kinds.has('sticker')
              ? 'sticker'
              : 'coins';
  return { rewards, kindForUi };
}

/** Called on boot and whenever the calendar day changes during play. */
export function grantDailyMail(draft: GameSave, content: ContentRegistry, nowWallMs: number): DomainEvent[] {
  const cycle = content.mailbox;
  if (!cycle) return [];
  const M = draft.mailbox;
  const today = dayKeyOf(nowWallMs);
  const last = M.lastGrantedDayKey;
  if (last !== null && today <= last) return [];
  const elapsed = last === null ? 1 : Math.max(1, dayIndex(today) - dayIndex(last));
  const n = Math.max(0, Math.min(elapsed, content.balance.mailboxCap - M.pending.length));
  const newIds: string[] = [];
  for (let i = 0; i < n; i++) {
    const cycleDay = (M.grantsCount % cycle.cycleLength) + 1;
    const { rewards, kindForUi } = materialize(draft, content, cycle, cycleDay);
    const mailId = `mail_${String(M.grantsCount)}`;
    M.pending.push({
      mailId,
      grantedForDayKey: fromIndex(dayIndex(today) - (n - 1 - i)),
      cycleDay,
      rewards,
      kindForUi,
    });
    M.grantsCount += 1;
    newIds.push(mailId);
  }
  M.lastGrantedDayKey = today;
  return newIds.length > 0 ? [{ type: 'MAIL_RECEIVED', mailIds: newIds, pending: M.pending.length }] : [];
}

export function claimMail(draft: GameSave, cmd: CommandOf<'claimMail'>, env: HandlerEnv): CommandResult {
  const M = draft.mailbox;
  const all = [...M.pending, ...M.special];
  const items = cmd.mailId === 'all' ? all : all.filter((m) => m.mailId === cmd.mailId);
  if (items.length === 0) return { ok: false, error: 'NOT_FOUND' };
  const events: DomainEvent[] = [];
  for (const item of items) {
    events.push(...grantRewards(draft, env.content, `mail:${item.mailId}`, item.rewards, 'mail'));
    M.claimedCount += 1;
    events.push({ type: 'MAIL_CLAIMED', mailId: item.mailId, rewards: item.rewards });
  }
  const claimed = new Set(items.map((m) => m.mailId));
  M.pending = M.pending.filter((m) => !claimed.has(m.mailId));
  M.special = M.special.filter((m) => !claimed.has(m.mailId));
  return { ok: true, events };
}
